import { ComponentType, ReactNode, Suspense } from "react";
import Landing from "@/pages/Landing";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import {
  AuthPageLoadingFallback,
  BlogRouteLoadingFallback,
  DefaultRouteLoadingFallback,
  FaqRouteLoadingFallback,
  AdminRouteLoadingFallback,
  FamilyRouteLoadingFallback,
  ProtectedRouteLoadingFallback,
  ReportsRouteLoadingFallback,
  SettingsRouteLoadingFallback,
} from "@/components/routing/RouteLoadingFallback";

// Lazy load components for code splitting
// Public routes - loaded on demand
// Landing is eagerly imported — homepage is the primary PSI/LCP entry point
const Auth = lazyWithRetry(() => import("@/pages/Auth"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));
const Terms = lazyWithRetry(() => import("@/pages/Terms"));
const Privacy = lazyWithRetry(() => import("@/pages/Privacy"));
const Refunds = lazyWithRetry(() => import("@/pages/Refunds"));
const OtherDetails = lazyWithRetry(() => import("@/pages/OtherDetails"));
const OtherDetailsLegacyRedirect = lazyWithRetry(
  () => import("@/pages/OtherDetailsLegacyRedirect")
);
const ContactUs = lazyWithRetry(() => import("@/pages/ContactUs"));
const ContactLegacyRedirect = lazyWithRetry(() => import("@/pages/ContactLegacyRedirect"));
const About = lazyWithRetry(() => import("@/pages/About"));
const UpdatePassword = lazyWithRetry(() => import("@/pages/UpdatePassword"));
const Features = lazyWithRetry(() => import("@/pages/Features"));
const FamilyWealthManagement = lazyWithRetry(() => import("@/pages/FamilyWealthManagement"));
const FAQ = lazyWithRetry(() => import("@/pages/FAQ"));
const Pricing = lazyWithRetry(() => import("@/pages/Pricing"));
const AnimatedLandingDemo = lazyWithRetry(() => import("@/pages/AnimatedLandingDemo"));
const Referrals = lazyWithRetry(() => import("@/pages/Referrals"));
const ReferralLanding = lazyWithRetry(() => import("@/pages/ReferralLanding"));
const Invite = lazyWithRetry(() => import("@/pages/Invite"));
const BlogList = lazyWithRetry(() => import("@/pages/blog/BlogList"));
const BlogSearch = lazyWithRetry(() => import("@/pages/blog/BlogSearch"));
const BlogPost = lazyWithRetry(() => import("@/pages/blog/BlogPost"));
const ShortUrlRedirect = lazyWithRetry(() => import("@/pages/blog/ShortUrlRedirect"));
const VerifyCompany = lazyWithRetry(() => import("@/pages/VerifyCompany"));
const AcceptDependentInvite = lazyWithRetry(() => import("@/pages/AcceptDependentInvite"));
const CreateCompany = lazyWithRetry(() => import("@/pages/CreateCompany"));
const PolicyAck = lazyWithRetry(() => import("@/pages/PolicyAck"));

// Protected routes - loaded on demand
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"));
const Profile = lazyWithRetry(() => import("@/pages/Profile"));
const Settings = lazyWithRetry(() => import("@/pages/Settings"));
const Assets = lazyWithRetry(() => import("@/pages/Assets"));
const Transactions = lazyWithRetry(() => import("@/pages/Transactions"));
const Goals = lazyWithRetry(() => import("@/pages/Goals"));
const Reports = lazyWithRetry(() => import("@/pages/Reports"));
const Preference = lazyWithRetry(() => import("@/pages/PreferenceLegacyRedirect"));
const Admin = lazyWithRetry(() => import("@/pages/Admin"));
const System = lazyWithRetry(() => import("@/pages/System"));
const Expenses = lazyWithRetry(() => import("@/pages/Expenses"));
const Account = lazyWithRetry(() => import("@/pages/AccountLegacyRedirect"));
const Investments = lazyWithRetry(() => import("@/pages/Investments"));
const Calculators = lazyWithRetry(() => import("@/pages/Calculators"));
const PublicCalculatorPage = lazyWithRetry(() => import("@/pages/PublicCalculatorPage"));
const Family = lazyWithRetry(() => import("@/pages/Family"));
const FamilyBinder = lazyWithRetry(() => import("@/pages/FamilyBinder"));
const FamilyMemberDetail = lazyWithRetry(() => import("@/pages/FamilyMemberDetail"));
const FamilySettings = lazyWithRetry(() => import("@/pages/FamilySettings"));
const FamilyHubLegacyRedirect = lazyWithRetry(() => import("@/pages/FamilyHubLegacyRedirect"));
const Subscription = lazyWithRetry(() => import("@/pages/Subscription"));
const Sheets = lazyWithRetry(() => import("@/pages/Sheets"));
const PortfolioAnalysis = lazyWithRetry(() => import("@/pages/PortfolioAnalysis"));
const NotificationTest = lazyWithRetry(() => import("@/pages/NotificationTest"));
const TestNotifications = lazyWithRetry(() => import("@/pages/TestNotifications"));
const APISetuDemo = lazyWithRetry(() => import("@/pages/APISetuDemo"));
const SplitMage = lazyWithRetry(() => import("@/pages/SplitMage"));
const SplitMageGroup = lazyWithRetry(() => import("@/pages/SplitMageGroup"));
const AcceptInvite = lazyWithRetry(() => import("@/pages/AcceptInvite"));
const FamilyInvitation = lazyWithRetry(() => import("@/pages/FamilyInvitation"));
const FamilyInviteLegacyRedirect = lazyWithRetry(
  () => import("@/pages/FamilyInviteLegacyRedirect")
);
const FamilyGoalShare = lazyWithRetry(() => import("@/pages/FamilyGoalShare"));
const ReferralDashboard = lazyWithRetry(() => import("@/pages/ReferralDashboard"));
const BlogEdit = lazyWithRetry(() => import("@/pages/blog/BlogEdit"));
const MySubmissions = lazyWithRetry(() => import("@/pages/blog/MySubmissions"));
const ModerationQueue = lazyWithRetry(() => import("@/pages/blog/ModerationQueue"));
const CommentModerationQueue = lazyWithRetry(() => import("@/pages/blog/CommentModerationQueue"));
const ModerationDetail = lazyWithRetry(() => import("@/pages/blog/ModerationDetail"));
const Collections = lazyWithRetry(() => import("@/pages/blog/Collections"));
const Leaderboard = lazyWithRetry(() => import("@/pages/blog/Leaderboard"));

// Define the route type to ensure proper typing
interface RouteConfig {
  path: string;
  element: ComponentType;
}

/**
 * Wraps a lazy-loaded component with Suspense for code splitting
 * @param Component - Lazy-loaded component
 * @returns Component wrapped with Suspense
 */
const withSuspense = <P extends object = object>(
  Component: ComponentType<P>,
  fallback: ReactNode = <DefaultRouteLoadingFallback />
) => {
  const Wrapped = (props: P) => (
    <Suspense fallback={fallback}>
      <Component {...props} />
    </Suspense>
  );
  return Wrapped;
};

const withBlogSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <BlogRouteLoadingFallback />);

const withFaqSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <FaqRouteLoadingFallback />);

const withProtectedSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <ProtectedRouteLoadingFallback />);

const withReportsSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <ReportsRouteLoadingFallback />);

const withSettingsSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <SettingsRouteLoadingFallback />);

const withFamilySuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <FamilyRouteLoadingFallback />);

const withAdminSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <AdminRouteLoadingFallback />);

const withAuthSuspense = <P extends object = object>(Component: ComponentType<P>) =>
  withSuspense(Component, <AuthPageLoadingFallback />);

// Public routes - accessible without authentication
export const publicRoutes: RouteConfig[] = [
  {
    path: "/",
    element: Landing,
  },
  {
    path: "/about",
    element: withSuspense(About),
  },
  {
    path: "/features",
    element: withSuspense(Features),
  },
  {
    path: "/family-wealth-management",
    element: withSuspense(FamilyWealthManagement),
  },
  {
    path: "/faq",
    element: withFaqSuspense(FAQ),
  },
  {
    path: "/pricing",
    element: withSuspense(Pricing),
  },
  {
    path: "/concepts/animated-landing",
    element: withSuspense(AnimatedLandingDemo),
  },
  {
    path: "/auth",
    element: withAuthSuspense(Auth),
  },
  {
    path: "/sign-in",
    element: withAuthSuspense(Auth), // Redirect to /auth (will show login by default)
  },
  {
    path: "/sign-up",
    element: withAuthSuspense(Auth), // Redirect to /auth with ?mode=signup
  },
  {
    path: "/update-password",
    element: withAuthSuspense(UpdatePassword),
  },
  {
    path: "/reset-password",
    element: withAuthSuspense(Auth), // Use /auth with ?mode=reset
  },
  {
    path: "/terms",
    element: withSuspense(Terms),
  },
  {
    path: "/privacy",
    element: withSuspense(Privacy),
  },
  {
    path: "/refunds",
    element: withSuspense(Refunds),
  },
  {
    path: "/products",
    element: withSuspense(OtherDetails),
  },
  {
    path: "/other-details",
    element: withSuspense(OtherDetailsLegacyRedirect),
  },
  {
    path: "/contact",
    element: withSuspense(ContactLegacyRedirect),
  },
  {
    path: "/contact-us",
    element: withSuspense(ContactUs),
  },
  {
    path: "/referrals",
    element: withSuspense(Referrals),
  },
  {
    path: "/calculators",
    element: withSuspense(Calculators),
  },
  {
    path: "/calculators/:slug",
    element: withSuspense(PublicCalculatorPage),
  },
  {
    path: "/r/:code",
    element: withSuspense(ReferralLanding),
  },
  {
    path: "/invite",
    element: withSuspense(Invite),
  },
  {
    path: "/join",
    element: withSuspense(Invite),
  },
  {
    path: "/blog",
    element: withBlogSuspense(BlogList),
  },
  {
    path: "/blog/search",
    element: withBlogSuspense(BlogSearch),
  },
  {
    path: "/blog/:slug",
    element: withBlogSuspense(BlogPost),
  },
  {
    path: "/b/:code",
    element: withSuspense(ShortUrlRedirect),
  },
  {
    path: "/verify-company",
    element: withSuspense(VerifyCompany),
  },
  {
    path: "/accept-dependent-invite",
    element: withSuspense(AcceptDependentInvite),
  },
  {
    path: "*",
    element: withSuspense(NotFound),
  },
];

// Protected routes - require authentication
// Wrap all lazy-loaded components with Suspense
export const protectedRoutes: RouteConfig[] = [
  {
    path: "/policy-ack",
    element: withProtectedSuspense(PolicyAck),
  },
  {
    path: "/dashboard",
    element: withProtectedSuspense(Dashboard),
  },
  {
    path: "/create-company",
    element: withProtectedSuspense(CreateCompany),
  },
  {
    path: "/profile",
    element: withProtectedSuspense(Profile),
  },
  {
    path: "/settings",
    element: withSettingsSuspense(Settings),
  },
  {
    path: "/assets",
    element: withProtectedSuspense(Assets),
  },
  {
    path: "/transactions",
    element: withProtectedSuspense(Transactions),
  },
  {
    path: "/goals",
    element: withProtectedSuspense(Goals),
  },
  {
    path: "/reports",
    element: withReportsSuspense(Reports),
  },
  {
    path: "/preference",
    element: withProtectedSuspense(Preference),
  },
  {
    path: "/admin",
    element: withAdminSuspense(Admin),
  },
  {
    path: "/system",
    element: withProtectedSuspense(System),
  },
  {
    path: "/expenses",
    element: withProtectedSuspense(Expenses),
  },
  {
    path: "/account",
    element: withProtectedSuspense(Account),
  },
  {
    path: "/investments",
    element: withProtectedSuspense(Investments),
  },
  {
    path: "/family",
    element: withFamilySuspense(Family),
  },
  {
    path: "/family/finances",
    element: withFamilySuspense(FamilyHubLegacyRedirect),
  },
  {
    path: "/family/people",
    element: withFamilySuspense(FamilyHubLegacyRedirect),
  },
  {
    path: "/family/life-admin",
    element: withFamilySuspense(FamilyHubLegacyRedirect),
  },
  {
    path: "/family/vault",
    element: withFamilySuspense(FamilyHubLegacyRedirect),
  },
  {
    path: "/family/activity",
    element: withFamilySuspense(FamilyHubLegacyRedirect),
  },
  {
    path: "/family/binder",
    element: withFamilySuspense(FamilyBinder),
  },
  {
    path: "/family/members/:memberId",
    element: withFamilySuspense(FamilyMemberDetail),
  },
  {
    path: "/family/settings",
    element: withFamilySuspense(FamilySettings),
  },
  {
    path: "/subscription",
    element: withProtectedSuspense(Subscription),
  },
  {
    path: "/sheets",
    element: withProtectedSuspense(Sheets),
  },
  {
    path: "/reports/analysis",
    element: withProtectedSuspense(PortfolioAnalysis),
  },
  {
    path: "/notifications/test",
    element: withProtectedSuspense(NotificationTest),
  },
  {
    path: "/test-notifications",
    element: withProtectedSuspense(TestNotifications),
  },
  {
    path: "/api-setu-demo",
    element: withProtectedSuspense(APISetuDemo),
  },
  {
    path: "/splitmage",
    element: withProtectedSuspense(SplitMage),
  },
  {
    path: "/splitmage/:groupId",
    element: withProtectedSuspense(SplitMageGroup),
  },
  {
    path: "/referrals/dashboard",
    element: withProtectedSuspense(ReferralDashboard),
  },
  // Specific blog routes MUST come before /blog/:slug
  // Note: /blog is in publicRoutes for unauthenticated access
  // Authenticated users will also access it via publicRoutes (BlogList handles layout conditionally)
  {
    path: "/blog/new/edit",
    element: withProtectedSuspense(BlogEdit),
  },
  {
    path: "/blog/submission/:submissionId/edit",
    element: withProtectedSuspense(BlogEdit),
  },
  {
    path: "/blog/my-submissions",
    element: withProtectedSuspense(MySubmissions),
  },
  {
    path: "/blog/moderation",
    element: withProtectedSuspense(ModerationQueue),
  },
  {
    path: "/blog/moderation/comments",
    element: withProtectedSuspense(CommentModerationQueue),
  },
  {
    path: "/blog/moderation/:versionId",
    element: withProtectedSuspense(ModerationDetail),
  },
  {
    path: "/blog/collections",
    element: withProtectedSuspense(Collections),
  },
  {
    path: "/blog/leaderboard",
    element: withProtectedSuspense(Leaderboard),
  },
  {
    path: "/blog/:slug/edit",
    element: withProtectedSuspense(BlogEdit),
  },
];

// Invite acceptance route (public - can be accessed without auth)
export const inviteRoutes: RouteConfig[] = [
  {
    path: "/invite/:token",
    element: withSuspense(AcceptInvite),
  },
  {
    path: "/family/invite/:token",
    element: withSuspense(FamilyInvitation),
  },
  // Backward compatibility for older email links
  {
    path: "/family/accept-invite",
    element: withSuspense(FamilyInviteLegacyRedirect),
  },
  {
    path: "/family/decline-invite",
    element: withSuspense(FamilyInviteLegacyRedirect),
  },
  {
    path: "/family/goal-share/:token",
    element: withSuspense(FamilyGoalShare),
  },
];

// All routes combined
export const routes: RouteConfig[] = [...publicRoutes, ...protectedRoutes, ...inviteRoutes];
